import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import {
  Trophy, Play, Pause, Heart, Crown, TiktokLogo, MusicNote,
  SpotifyLogo, ChartLineUp, Copyright, Star, Gift, CheckCircle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDeviceId = () => {
  let id = localStorage.getItem("device_id");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    localStorage.setItem("device_id", id);
  }
  return id;
};

const BENEFITS = [
  { icon: SpotifyLogo, title: "Distribución real", text: "Subimos tu canción a Spotify y Apple Music (vía DistroKid) — nosotros lo pagamos." },
  { icon: ChartLineUp, title: "Promoción pagada", text: "Invertimos en TikTok Ads para viralizar tu canción y tu cuenta de creador." },
  { icon: Copyright, title: "Derechos de autor", text: "Te asesoramos para registrar tu canción y empieces a recibir regalías." },
  { icon: Star, title: "Playlist oficial", text: "Portada de la playlist oficial de Tu MusiKa en Spotify durante todo el mes." },
  { icon: CheckCircle, title: "Creador Destacado", text: "Insignia especial de Creador Destacado dentro de la plataforma." },
  { icon: Gift, title: "Regalos", text: "1 año de música premium, licencias de producción (iZotope/Ableton Lite), audífonos Sony/JBL/Beats, MIDI Arturia/Akai o bocinas bluetooth." },
];

const RULES = [
  { n: "1", title: "Hazlo nativo de TikTok", text: "Sube un video a TikTok usando el audio de tu canción con el hashtag #TuMusiKa. ¡Eso también te da puntos con el jurado!" },
  { n: "2", title: "Votación transparente", text: "La comunidad vota aquí. El Top 5 más votado pasa a la final y un jurado neutral elige al ganador definitivo (cero bots)." },
  { n: "3", title: "Premio inmediato", text: "Los premios digitales se entregan al instante en cuanto termina el concurso." },
];

export default function Concurso() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = () =>
    axios.get(`${API}/contest/entries`, { params: { device_id: getDeviceId() } })
      .then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => setPlayingId(null);
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
  }, []);

  const toggle = (e) => {
    const a = audioRef.current;
    if (!a || !e.audio_url) return;
    if (playingId === e.id) { a.pause(); setPlayingId(null); return; }
    a.src = e.audio_url.startsWith("http") ? e.audio_url : `${process.env.REACT_APP_BACKEND_URL}${e.audio_url}`;
    a.volume = 0.8;
    a.play().then(() => setPlayingId(e.id)).catch(() => setPlayingId(null));
  };

  const voteFor = async (e) => {
    try {
      const { data: res } = await axios.post(`${API}/contest/vote`, { entry_id: e.id, device_id: getDeviceId() });
      toast.success("¡Voto registrado! 🗳️");
      setData(d => ({
        ...d,
        entries: d.entries.map(x => x.id === e.id ? { ...x, votes: res.votes, voted: true } : x),
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo votar");
    }
  };

  const entries = data?.entries || [];
  const top = entries[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <audio ref={audioRef} data-testid="contest-audio" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#FFB800] flex items-center justify-center mb-4">
            <Trophy size={30} weight="fill" className="text-black" />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#FFB800] font-bold mb-2">Concurso del mes · {data?.label || "Julio 2026"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight mb-3">
            Tu historia puede sonar<br />en <span className="text-[#B4F34A]">Spotify</span>
          </h1>
          <p className="text-white/60 max-w-xl mx-auto">
            Cada mes la comunidad vota por su canción favorita. Al ganador le impulsamos
            su carrera como creador de música digital: distribución, promoción y regalos.
          </p>
        </div>

        {/* Most voted highlight */}
        {top && (
          <div className="rounded-3xl border-2 border-[#FFB800]/50 bg-gradient-to-r from-[#FFB800]/10 to-transparent p-5 sm:p-6 mb-8" data-testid="contest-top-song">
            <div className="flex items-center gap-2 mb-3">
              <Crown size={18} weight="fill" className="text-[#FFB800]" />
              <p className="text-xs uppercase tracking-widest font-bold text-[#FFB800]">
                {top.winner ? "Ganadora del mes" : "La más votada del mes"}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl overflow-hidden shrink-0 bg-[#1a1a1a]">
                {top.cover_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${top.cover_url}`} alt={top.title} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <button onClick={() => toggle(top)} className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors" data-testid="contest-top-play">
                  {playingId === top.id ? <Pause size={26} weight="fill" className="text-[#FFB800]" /> : <Play size={26} weight="fill" className="text-white" />}
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display font-bold text-xl sm:text-2xl truncate">{top.title}</p>
                <p className="text-white/50 text-sm truncate">Por {top.artist_name} · {top.genre_name}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="inline-flex items-center gap-1.5 text-[#FF4B00] font-bold text-sm">
                    <Heart size={15} weight="fill" /> {top.votes} votos
                  </span>
                  {top.tiktok_url && (
                    <a href={top.tiktok_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white">
                      <TiktokLogo size={14} /> Ver en TikTok
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Entries */}
        <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
          <MusicNote size={18} weight="fill" className="text-[#FFB800]" />
          Canciones participantes {entries.length > 0 && <span className="text-white/40 font-normal text-sm">({entries.length})</span>}
        </h2>

        {entries.length === 0 ? (
          <div className="rounded-2xl bg-[#121212] border border-white/5 p-8 text-center mb-10" data-testid="contest-empty">
            <p className="font-display font-bold text-lg mb-1">Aún no hay participantes</p>
            <p className="text-white/50 text-sm mb-4">¡Sé el primero! Crea tu canción y participa desde "Mis canciones".</p>
            <Link to={user ? "/create" : "/register"} className="inline-block px-6 py-3 rounded-full bg-[#FFB800] text-black font-semibold" data-testid="contest-empty-cta">
              Crear mi canción
            </Link>
          </div>
        ) : (
          <div className="space-y-3 mb-12" data-testid="contest-entries">
            {entries.map((e, i) => (
              <div key={e.id} className={`rounded-2xl border p-3 sm:p-4 flex items-center gap-3 sm:gap-4 ${e.winner ? "border-[#FFB800]/60 bg-[#FFB800]/5" : "bg-[#121212] border-white/5"}`} data-testid={`contest-entry-${e.id}`}>
                <span className="font-display font-black text-2xl w-8 text-center" style={{ color: i < 5 ? "#FFB800" : "rgba(255,255,255,0.2)" }}>{i + 1}</span>
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden shrink-0 bg-[#1a1a1a]">
                  {e.cover_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${e.cover_url}`} alt={e.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                  <button onClick={() => toggle(e)} aria-label={`Reproducir ${e.title}`} className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors" data-testid={`contest-play-${e.id}`}>
                    {playingId === e.id ? <Pause size={20} weight="fill" className="text-[#FFB800]" /> : <Play size={20} weight="fill" className="text-white" />}
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold truncate flex items-center gap-1.5">
                    {e.winner && <Crown size={14} weight="fill" className="text-[#FFB800] shrink-0" />}
                    {e.title}
                  </p>
                  <p className="text-xs text-white/50 truncate">Por {e.artist_name} · {e.genre_name}</p>
                  {e.tiktok_url && (
                    <a href={e.tiktok_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-white mt-0.5">
                      <TiktokLogo size={11} /> TikTok
                    </a>
                  )}
                </div>
                <button
                  onClick={() => !e.voted && voteFor(e)}
                  disabled={e.voted}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold transition-all active:scale-95 ${e.voted ? "bg-[#FF4B00]/15 text-[#FF4B00] cursor-default" : "bg-white/5 hover:bg-[#FF4B00] hover:text-white border border-white/10"}`}
                  data-testid={`contest-vote-${e.id}`}
                >
                  <Heart size={15} weight={e.voted ? "fill" : "regular"} /> {e.votes}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Benefits */}
        <h2 className="font-display text-2xl sm:text-3xl font-black text-center mb-2">¿Qué gana el ganador? 🏆</h2>
        <p className="text-white/50 text-center text-sm mb-6 max-w-lg mx-auto">Impulsamos tu carrera como creador de música digital — esto no lo hace nadie más.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12" data-testid="contest-benefits">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl bg-[#121212] border border-white/5 p-5">
              <b.icon size={24} weight="duotone" className="text-[#B4F34A] mb-2" />
              <p className="font-display font-bold mb-1">{b.title}</p>
              <p className="text-xs text-white/55 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>

        {/* Rules */}
        <h2 className="font-display text-2xl font-black text-center mb-6">Reglas de oro 📜</h2>
        <div className="space-y-3 max-w-2xl mx-auto mb-12" data-testid="contest-rules">
          {RULES.map((r) => (
            <div key={r.n} className="flex gap-4 rounded-2xl bg-[#121212] border border-white/5 p-5">
              <span className="font-display font-black text-3xl text-[#FFB800] shrink-0">{r.n}</span>
              <div>
                <p className="font-display font-bold mb-1">{r.title}</p>
                <p className="text-sm text-white/55">{r.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center pb-10">
          <p className="font-display text-xl font-bold mb-3">¿Listo para participar?</p>
          <Link
            to={user ? "/library" : "/register"}
            className="inline-block px-8 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-bold transition-all active:scale-95"
            data-testid="contest-cta"
          >
            {user ? "Participar con mi canción" : "Crear mi canción y participar"}
          </Link>
          <p className="text-xs text-white/40 mt-3">Participa desde la página de tu canción terminada con el botón "Participar en el concurso".</p>
        </div>
      </div>
    </div>
  );
}
